import { Box, CircularProgress } from '@mui/material';
import React, { FunctionComponent } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';

import { AdminDjRequests } from '../admin/AdminDjRequests/AdminDjRequests.page';
import { AdminEventTransactions } from '../admin/AdminEventTransactions/AdminEventTransactions.page';
import { AdminEventUsers } from '../admin/AdminEventUsers/AdminEventUsers.page';
import { AdminEvents } from '../admin/AdminEvents/AdminEvents.page';
import { AdminUsers } from '../admin/AdminUsers/AdminUsers.page';
import { AdminValidation } from '../admin/AdminValidation/AdminValidation.page';
import { CreateAccount } from '../auth/CreateAccount/CreateAccount.page';
import { ForgotPassword } from '../auth/ForgotPassword/ForgotPassword.page';
import { Login } from '../auth/Login/Login.page';
import { Carpool } from '../carpool/pages/Carpool/Carpool.page';
import { Contact } from '../contact/pages/Contact/Contact.page';
import { Dashboard } from '../dashboard/Dashboard.page';
import { DjRequest } from '../dj/pages/DjRequest/DjRequest.page';
import { Caution } from '../event/pages/Caution/Caution.page';
import { EventRegister } from '../event/pages/EventRegister/EventRegister.page';
import { EventUsers } from '../event/pages/EventUsers/EventUsers.page';
import { Payment } from '../event/pages/Payment/Payment.page';
import { PaymentError } from '../event/pages/PaymentError/PaymentError.page';
import { PaymentForSomeone } from '../event/pages/PaymentForSomeone/PaymentForSomeone.page';
import { PaymentSuccess } from '../event/pages/PaymentSuccess/PaymentSuccess.page';
import { Home } from '../home/Home.page';
import { LegalMentions } from '../legal/pages/LegalMentions.page';
import { Bedrooms } from '../place/pages/Bedrooms/Bedrooms.page';
import { Places } from '../place/pages/Places/Places.page';
import { TargetedUsers } from '../targets/pages/TargetedUsers/TargetedUsers.page';
import { Targets } from '../targets/pages/Targets/Targets.page';
import { TinderedUsers } from '../tinders/pages/TinderedUsers/TinderedUsers.page';
import { Tinders } from '../tinders/pages/Tinders/Tinders.page';
import { useCurrentUserQuery } from '../user/hooks/useCurrentUserQuery';
import { UserImages } from '../user/pages/UserImages/UserImages.page';
import { UserInfos } from '../user/pages/UserInfos/UserInfos.page';
import { NotFound } from './NotFound';

interface RootNavigatorProps {
  isLoggedIn: boolean;
}

export const RootNavigator: FunctionComponent<RootNavigatorProps> = ({
  isLoggedIn,
}) => {
  const { data: currentUserData, loading } = useCurrentUserQuery(true);

  const isAdmin = currentUserData?.currentUser?.isAdmin ?? false;
  const isValidator = currentUserData?.currentUser?.isValidator ?? false;

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/legal" element={<LegalMentions />} />
        <Route
          path="/dashboard"
          element={isLoggedIn ? <Dashboard /> : <Navigate to="/login" />}
        />
        <Route
          path="/personal-information"
          element={isLoggedIn ? <UserInfos /> : <Navigate to="/login" />}
        />
        <Route
          path="/images"
          element={isLoggedIn ? <UserImages /> : <Navigate to="/login" />}
        />
        <Route
          path="/event-register"
          element={isLoggedIn ? <EventRegister /> : <Navigate to="/login" />}
        />
        <Route
          path="/caution"
          element={isLoggedIn ? <Caution /> : <Navigate to="/login" />}
        />
        <Route
          path="/payment"
          element={isLoggedIn ? <Payment /> : <Navigate to="/login" />}
        />
        <Route
          path="/payment/for-someone"
          element={
            isLoggedIn ? <PaymentForSomeone /> : <Navigate to="/login" />
          }
        />
        <Route
          path="/payment/success"
          element={isLoggedIn ? <PaymentSuccess /> : <Navigate to="/login" />}
        />
        <Route
          path="/payment/error"
          element={isLoggedIn ? <PaymentError /> : <Navigate to="/login" />}
        />
        <Route
          path="/event-users"
          element={isLoggedIn ? <EventUsers /> : <Navigate to="/login" />}
        />
        <Route
          path="/bedrooms"
          element={isLoggedIn ? <Bedrooms /> : <Navigate to="/login" />}
        />
        <Route
          path="/covoiturage"
          element={isLoggedIn ? <Carpool /> : <Navigate to="/login" />}
        />
        <Route
          path="/target"
          element={isLoggedIn ? <Targets /> : <Navigate to="/login" />}
        />
        <Route
          path="/target/stats"
          element={isLoggedIn ? <TargetedUsers /> : <Navigate to="/login" />}
        />
        <Route
          path="/tinder"
          element={isLoggedIn ? <Tinders /> : <Navigate to="/login" />}
        />
        <Route
          path="/tinder/stats"
          element={isLoggedIn ? <TinderedUsers /> : <Navigate to="/login" />}
        />
        <Route
          path="/dj"
          element={isLoggedIn ? <DjRequest /> : <Navigate to="/login" />}
        />
        <Route
          path="/places"
          element={isLoggedIn ? <Places /> : <Navigate to="/login" />}
        />
        <Route
          path="/contact"
          element={isLoggedIn ? <Contact /> : <Navigate to="/dashboard" />}
        />

        <Route
          path="/login"
          element={isLoggedIn ? <Navigate to="/dashboard" /> : <Login />}
        />
        <Route
          path="/create-account"
          element={
            isLoggedIn ? <Navigate to="/dashboard" /> : <CreateAccount />
          }
        />
        <Route
          path="/forgot-password"
          element={
            isLoggedIn ? <Navigate to="/dashboard" /> : <ForgotPassword />
          }
        />

        {isAdmin ? (
          <>
            <Route
              path="/events"
              element={isLoggedIn ? <AdminEvents /> : <Navigate to="/login" />}
            />
            <Route
              path="/admin-bedrooms"
              element={
                isLoggedIn ? <Bedrooms isAdminView /> : <Navigate to="/login" />
              }
            />
            <Route
              path="/admin-event-users"
              element={
                isLoggedIn ? <AdminEventUsers /> : <Navigate to="/login" />
              }
            />
            <Route
              path="/admin-dj-requests"
              element={
                isLoggedIn ? <AdminDjRequests /> : <Navigate to="/login" />
              }
            />
            <Route
              path="/event-transactions"
              element={
                isLoggedIn ? (
                  <AdminEventTransactions />
                ) : (
                  <Navigate to="/login" />
                )
              }
            />
            <Route
              path="/users"
              element={isLoggedIn ? <AdminUsers /> : <Navigate to="/login" />}
            />
          </>
        ) : null}

        {isLoggedIn ? (
          <Route
            path="/validation"
            element={
              isValidator ? <AdminValidation /> : <Navigate to="/login" />
            }
          />
        ) : null}

        <Route path="*" element={<NotFound />} />
      </Routes>
    </BrowserRouter>
  );
};
